/**
 * lobbyFacilities.ts - Unified lobby facility type definitions
 *
 * All lobby building floors use facilities from this unified list.
 * Each facility affects visitor patience, generates revenue, and/or boosts atmosphere.
 * Formerly split between lobbyRooms (building floors) and lobbyFacilities (grid).
 */

import type { LobbyPropId } from './lobbyProps.js';

export type LobbyFacilityType =
  | 'ticket_booth'
  | 'waiting_lounge'
  | 'vip_lounge'
  | 'poster_wall'
  | 'snack_cart'
  | 'souvenir_booth'
  | 'photo_zone'
  | 'tv_monitor'
  | 'costume_rental'
  | 'fortune_booth';

/** Unique effect type for each lobby facility */
export type UniqueEffectType =
  | 'ticket_speed'        // ticket_booth: reduces entry interval
  | 'max_queue_up'        // waiting_lounge: increases max queue size
  | 'vip_rate_up'         // vip_lounge: doubles VIP visitor appearance rate
  | 'satisfaction_up'     // poster_wall: base satisfaction bonus for visitors
  | 'emergency_patience'  // snack_cart: recovers patience for low-patience visitors
  | 'revisit_rate_up'     // souvenir_booth: next day visitor inflow bonus
  | 'reputation_per_day'  // photo_zone: daily reputation gain from SNS shares
  | 'anticipation_up'     // tv_monitor: fear preview boosts anticipation
  | 'fun_bonus'           // costume_rental: visitor fun stat bonus
  | 'random_sp_bonus';    // fortune_booth: random bonus SP event chance

export interface UniqueEffect {
  type: UniqueEffectType;
  value: number;           // effect magnitude (interpretation depends on type)
  descKey: string;         // i18n key for effect description
}

export interface LobbyFacilityDef {
  id: LobbyFacilityType;
  nameKey: string;
  descKey: string;
  cost: number;
  /** SP revenue per visitor per tick (scaled by queue size) */
  revPerVisitor: number;
  /** Patience decay reduction (0-1, stacks additively with other facilities) */
  patienceModifier: number;
  maxLevel: number;
  upgradeCostMultiplier: number;
  /** Revenue multiplier per upgrade level */
  revenuePerLevel: number;
  /** Patience modifier bonus per upgrade level */
  patiencePerLevel: number;
  /** Icon image path for the facility card */
  imagePath: string;
  /** Unique effect specific to this facility */
  uniqueEffect: UniqueEffect;
  /** Props available for purchase on this facility's floor */
  availableProps: LobbyPropId[];
}

export const LOBBY_FACILITY_DEFS: Record<LobbyFacilityType, LobbyFacilityDef> = {
  ticket_booth: {
    id: 'ticket_booth',
    nameKey: 'lf_ticket_booth',
    descKey: 'lf_ticket_booth_desc',
    cost: 0,
    revPerVisitor: 0,
    patienceModifier: 0,
    maxLevel: 3,
    upgradeCostMultiplier: 2.0,
    revenuePerLevel: 0,
    patiencePerLevel: 0,
    imagePath: '/images/lobby/lobby-ticket-booth.png',
    uniqueEffect: { type: 'ticket_speed', value: 0.15, descKey: 'lf_ue_ticket_speed' },
    availableProps: ['info_sign', 'candle', 'cobweb', 'pumpkin'],
  },
  waiting_lounge: {
    id: 'waiting_lounge',
    nameKey: 'lf_waiting_lounge',
    descKey: 'lf_waiting_lounge_desc',
    cost: 800,
    revPerVisitor: 0,
    patienceModifier: 0.2,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    revenuePerLevel: 0,
    patiencePerLevel: 0.05,
    imagePath: '/images/lobby/lobby-waiting-lounge.png',
    uniqueEffect: { type: 'max_queue_up', value: 5, descKey: 'lf_ue_max_queue_up' },
    availableProps: ['sofa', 'tv_screen', 'vending_machine', 'potted_plant', 'magazine_rack'],
  },
  vip_lounge: {
    id: 'vip_lounge',
    nameKey: 'lf_vip_lounge',
    descKey: 'lf_vip_lounge_desc',
    cost: 3000,
    revPerVisitor: 20,
    patienceModifier: 0.1,
    maxLevel: 3,
    upgradeCostMultiplier: 3.5,
    revenuePerLevel: 10,
    patiencePerLevel: 0.03,
    imagePath: '/images/lobby/lobby-vip-lounge.png',
    uniqueEffect: { type: 'vip_rate_up', value: 2, descKey: 'lf_ue_vip_rate_up' },
    availableProps: ['chandelier', 'wine_glass', 'velvet_curtain', 'gold_frame'],
  },
  poster_wall: {
    id: 'poster_wall',
    nameKey: 'lf_poster_wall',
    descKey: 'lf_poster_wall_desc',
    cost: 300,
    revPerVisitor: 0,
    patienceModifier: 0.1,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    revenuePerLevel: 0,
    patiencePerLevel: 0.03,
    imagePath: '/images/lobby/lobby-poster-wall.png',
    uniqueEffect: { type: 'satisfaction_up', value: 0.2, descKey: 'lf_ue_satisfaction_up' },
    availableProps: ['picture_frame', 'spotlight', 'cobweb', 'skull_ornament'],
  },
  snack_cart: {
    id: 'snack_cart',
    nameKey: 'lf_snack_cart',
    descKey: 'lf_snack_cart_desc',
    cost: 500,
    revPerVisitor: 5,
    patienceModifier: 0.3,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    revenuePerLevel: 3,
    patiencePerLevel: 0.05,
    imagePath: '/images/lobby/lobby-snack-cart.png',
    uniqueEffect: { type: 'emergency_patience', value: 15, descKey: 'lf_ue_emergency_patience' },
    availableProps: ['pumpkin_cookie', 'ghost_drink', 'info_sign', 'napkin_holder'],
  },
  souvenir_booth: {
    id: 'souvenir_booth',
    nameKey: 'lf_souvenir_booth',
    descKey: 'lf_souvenir_booth_desc',
    cost: 800,
    revPerVisitor: 8,
    patienceModifier: 0,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    revenuePerLevel: 4,
    patiencePerLevel: 0,
    imagePath: '/images/lobby/lobby-souvenir.png',
    uniqueEffect: { type: 'revisit_rate_up', value: 0.15, descKey: 'lf_ue_revisit_rate_up' },
    availableProps: ['display_shelf', 'plush_doll', 'keychain_rack', 'wrapping_paper'],
  },
  photo_zone: {
    id: 'photo_zone',
    nameKey: 'lf_photo_zone',
    descKey: 'lf_photo_zone_desc',
    cost: 1000,
    revPerVisitor: 10,
    patienceModifier: 0.1,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    revenuePerLevel: 5,
    patiencePerLevel: 0.03,
    imagePath: '/images/lobby/lobby-photo-zone.png',
    uniqueEffect: { type: 'reputation_per_day', value: 2, descKey: 'lf_ue_reputation_per_day' },
    availableProps: ['backdrop', 'prop_box', 'spotlight', 'mask_stand'],
  },
  tv_monitor: {
    id: 'tv_monitor',
    nameKey: 'lf_tv_monitor',
    descKey: 'lf_tv_monitor_desc',
    cost: 600,
    revPerVisitor: 0,
    patienceModifier: 0.2,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    revenuePerLevel: 0,
    patiencePerLevel: 0.05,
    imagePath: '/images/lobby/lobby-tv-monitor.png',
    uniqueEffect: { type: 'anticipation_up', value: 0.4, descKey: 'lf_ue_anticipation_up' },
    availableProps: ['antenna', 'remote_control', 'speaker', 'cable_wires'],
  },
  costume_rental: {
    id: 'costume_rental',
    nameKey: 'lf_costume_rental',
    descKey: 'lf_costume_rental_desc',
    cost: 1200,
    revPerVisitor: 15,
    patienceModifier: 0.05,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    revenuePerLevel: 7,
    patiencePerLevel: 0.02,
    imagePath: '/images/lobby/lobby-costume.png',
    uniqueEffect: { type: 'fun_bonus', value: 15, descKey: 'lf_ue_fun_bonus' },
    availableProps: ['clothes_rack', 'mirror', 'makeup_table', 'wig_stand'],
  },
  fortune_booth: {
    id: 'fortune_booth',
    nameKey: 'lf_fortune_booth',
    descKey: 'lf_fortune_booth_desc',
    cost: 1500,
    revPerVisitor: 12,
    patienceModifier: 0.15,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    revenuePerLevel: 6,
    patiencePerLevel: 0.04,
    imagePath: '/images/lobby/lobby-fortune.png',
    uniqueEffect: { type: 'random_sp_bonus', value: 0.1, descKey: 'lf_ue_random_sp_bonus' },
    availableProps: ['crystal_ball', 'tarot_cards', 'curtain', 'candelabra'],
  },
};

export const ALL_LOBBY_FACILITY_TYPES = Object.keys(LOBBY_FACILITY_DEFS) as LobbyFacilityType[];

/** Build order for lobby floor facilities. ticket_booth is pre-installed at level 1. */
export const LOBBY_FACILITY_BUILD_ORDER: LobbyFacilityType[] = [
  'waiting_lounge',
  'poster_wall',
  'snack_cart',
  'tv_monitor',
  'vip_lounge',
  'souvenir_booth',
  'photo_zone',
  'costume_rental',
  'fortune_booth',
];

export function lobbyFacilityUpgradeCost(type: LobbyFacilityType, toLevel: number): number {
  const def = LOBBY_FACILITY_DEFS[type];
  if (!def) return 0;
  const base = Math.max(def.cost, 200);
  return Math.floor(base * Math.pow(def.upgradeCostMultiplier, toLevel - 1));
}
